/**
 * D2 helpers: wrap company Property media/parcel transports so each write is
 * bound to the live scopeGate authority operation. Personal Realty defaults
 * remain untouched — callers inject these wrappers only on company surfaces.
 */

function isAuthorityLoss(err) {
  const status = err?.response?.status;
  return status === 403 || status === 404;
}

function staleAuthorityError() {
  const stale = new Error("stale_authority");
  stale.code = "STALE_AUTHORITY";
  return stale;
}

async function runBoundMutation({
  scopeGate,
  getAuthority,
  mountedRef,
  onAuthorityLoss,
  onStale,
  transportFn,
  afterSuccess,
}) {
  const auth = getAuthority();
  const op = scopeGate.beginMutation({
    companyId: auth.companyId,
    actorId: auth.actorId,
    resourceId: auth.resourceId,
    membershipGeneration: auth.membershipGeneration,
  });
  // R2-3: reject before dispatch so a stale captured op never hits the network.
  if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
    if (onStale) onStale();
    throw staleAuthorityError();
  }
  try {
    const result = await transportFn();
    if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
      if (onStale) onStale();
      throw staleAuthorityError();
    }
    if (typeof afterSuccess === "function") {
      await afterSuccess(op);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        if (onStale) onStale();
        throw staleAuthorityError();
      }
    }
    return result;
  } catch (err) {
    if (err?.code === "STALE_AUTHORITY") throw err;
    if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
      if (onStale) onStale();
      throw staleAuthorityError();
    }
    // 403/404 callbacks only for the still-current operation.
    if (isAuthorityLoss(err) && onAuthorityLoss) {
      onAuthorityLoss(err.response.status);
    }
    throw err;
  }
}

export function createAuthorityBoundMediaApi({
  transport,
  scopeGate,
  getAuthority,
  mountedRef,
  onAuthorityLoss,
  onStale,
}) {
  const run = (transportFn) =>
    runBoundMutation({
      scopeGate,
      getAuthority,
      mountedRef,
      onAuthorityLoss,
      onStale,
      transportFn,
    });

  return {
    uploadPropertyImages: (id, files, kind) =>
      run(() => transport.uploadPropertyImages(id, files, kind)),
    patchPropertyImage: (id, imageId, body) =>
      run(() => transport.patchPropertyImage(id, imageId, body)),
    deletePropertyImage: (id, imageId) =>
      run(() => transport.deletePropertyImage(id, imageId)),
    reorderPropertyImages: (id, kind, imageIds) =>
      run(() => transport.reorderPropertyImages(id, kind, imageIds)),
    propertyImageUploadErrorMessage: transport.propertyImageUploadErrorMessage,
  };
}

export function createAuthorityBoundLandParcelsApi({
  transport,
  scopeGate,
  getAuthority,
  mountedRef,
  onAuthorityLoss,
  onStale,
  onRefresh,
  fetchDetail,
}) {
  const parcels = transport.landParcels || transport;
  const run = (transportFn, { refresh = false } = {}) =>
    runBoundMutation({
      scopeGate,
      getAuthority,
      mountedRef,
      onAuthorityLoss,
      onStale,
      transportFn,
      afterSuccess: refresh
        ? async (op) => {
            // R2-3 two-phase refresh: fetch → validate → only then apply.
            if (typeof fetchDetail === "function") {
              const detail = await fetchDetail();
              if (
                !scopeGate.mutationStillValid(op, {
                  mounted: mountedRef.current,
                })
              ) {
                if (onStale) onStale();
                throw staleAuthorityError();
              }
              if (typeof onRefresh === "function") {
                await onRefresh(detail, op);
              }
              return;
            }
            if (typeof onRefresh === "function") {
              await onRefresh(undefined, op);
            }
          }
        : undefined,
    });

  return {
    listLandParcels: (propertyId) => parcels.listLandParcels(propertyId),
    createLandParcel: (propertyId, payload) =>
      run(() => parcels.createLandParcel(propertyId, payload), {
        refresh: true,
      }),
    patchLandParcel: (propertyId, parcelId, payload) =>
      run(() => parcels.patchLandParcel(propertyId, parcelId, payload), {
        refresh: true,
      }),
    deleteLandParcel: (propertyId, parcelId) =>
      run(() => parcels.deleteLandParcel(propertyId, parcelId), {
        refresh: true,
      }),
  };
}

export function membershipGenerationOf(membership) {
  if (!membership || membership.membership_generation == null) return null;
  return membership.membership_generation;
}
